import React, { createContext, useCallback, useContext, useMemo, useState } from "react";

/**
 * Tracks how many AssetUploadField uploads are in flight at once so the
 * surrounding form's Save button can disable itself while any upload runs —
 * preventing the admin from submitting an event payload that still references
 * a key that hasn't come back from the backend yet.
 *
 * Per docs/01-frontend-integration.md §"Recommended UX": "Disable Save while
 * any upload is in progress to avoid sending stale keys."
 */
interface Ctx {
  count: number;
  begin: () => void;
  end: () => void;
}

const AssetUploadingContext = createContext<Ctx | null>(null);

export const AssetUploadingProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [count, setCount] = useState(0);
  const begin = useCallback(() => setCount((c) => c + 1), []);
  const end = useCallback(() => setCount((c) => Math.max(0, c - 1)), []);
  const value = useMemo(() => ({ count, begin, end }), [count, begin, end]);
  return (
    <AssetUploadingContext.Provider value={value}>
      {children}
    </AssetUploadingContext.Provider>
  );
};

/** For AssetUploadField (or similar) to bracket its upload with begin/end. */
export const useAssetUploadingTracker = () => {
  const ctx = useContext(AssetUploadingContext);
  return {
    begin: () => ctx?.begin(),
    end: () => ctx?.end(),
  };
};

/** For the page that owns the Save button to disable it while uploads run. */
export const useIsAnyAssetUploading = (): boolean => {
  const ctx = useContext(AssetUploadingContext);
  return (ctx?.count ?? 0) > 0;
};
